import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import { withRouter } from "react-router-dom";
import deepResearchStore from "Stores/deepResearchData";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import { toasterState } from "Components/common/toaster";
import SellerProfileFormBody, {
  buildEmptyProfile,
  hydrateProfileForEdit
} from "Pages/Dashboard/DeepResearch/components/SellerProfileFormBody";

// Stable JSON for dirty comparison (key order independent enough for our use).
const stableJson = (v) => {
  try {
    return JSON.stringify(v);
  } catch (e) {
    return "";
  }
};

const SellerProfilePage = observer(({ match, location, history }) => {
  const profileId = match?.params?.id || null;
  const isCreate = !profileId;

  // Profile loaded on mount: either existing from store, or auto-fill draft from
  // route state (set by the URL-prompt popup), or an empty shell.
  const [profile, setProfile] = useState(() => {
    if (profileId) {
      const existing = deepResearchStore.sellerProfiles.find(
        (p) => p.id === profileId
      );
      return existing ? hydrateProfileForEdit(existing) : buildEmptyProfile();
    }
    const draft = location?.state?.draft;
    return draft ? hydrateProfileForEdit(draft) : buildEmptyProfile();
  });
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // Capture the "as-loaded" snapshot for dirty tracking.
  const [baseline, setBaseline] = useState(() => stableJson(profile));

  // If the store hadn't finished loading when we mounted, watch for the profile
  // to appear and re-hydrate.
  useEffect(() => {
    if (!profileId) return;
    if (deepResearchStore.sellerProfiles.length === 0) {
      deepResearchStore.fetchSellerProfiles({ force: false });
      return;
    }
    const existing = deepResearchStore.sellerProfiles.find(
      (p) => p.id === profileId
    );
    if (existing) {
      const hydrated = hydrateProfileForEdit(existing);
      setProfile(hydrated);
      setBaseline(stableJson(hydrated));
      setNotFound(false);
    } else if (deepResearchStore.sellerProfilesLoaded) {
      setNotFound(true);
    }
  }, [profileId, deepResearchStore.sellerProfiles, deepResearchStore.sellerProfilesLoaded]);

  const dirty = useMemo(
    () => stableJson(profile) !== baseline,
    [profile, baseline]
  );

  const goToList = () => history.push("/settings/seller-profiles");

  const handleBack = () => {
    if (!dirty) {
      goToList();
      return;
    }
    confirmationPopupState.setPopupValues({
      content:
        "Discard your changes? Anything you've entered on this page will be lost.",
      actionBtnText: "Discard changes",
      cancelBtnText: "Keep editing",
      callback: () => {
        confirmationPopupState.setShowPopup(false);
        goToList();
      }
    });
    confirmationPopupState.setShowPopup(true);
  };

  const handleSave = () => {
    setSaving(true);
    const payload = profileId
      ? { ...profile, id: profileId, isActive: true }
      : { ...profile, isActive: true };
    deepResearchStore
      .saveSellerProfile(payload)
      .then((saved) => {
        setSaving(false);
        if (saved && saved.id) {
          toasterState.setToastMsg(
            isCreate ? "Seller profile created" : "Seller profile updated",
            "success"
          );
          // Reset baseline so the back button doesn't prompt for a saved page.
          setBaseline(stableJson(profile));
          goToList();
        } else {
          toasterState.setToastMsg("Could not save seller profile", "failure");
        }
      })
      .catch(() => {
        setSaving(false);
        toasterState.setToastMsg("Could not save seller profile", "failure");
      });
  };

  const headerName =
    profile.name ||
    profile.company_name ||
    profile.product_name ||
    (isCreate ? "New seller profile" : "Untitled profile");

  if (notFound) {
    return (
      <section className="sellerProfilePageWrapper">
        <div className="sellerProfilePageNotFound">
          <h3>Seller profile not found</h3>
          <p>This profile may have been deleted.</p>
          <button className="drPopupBtn primary" onClick={goToList}>
            ← Back to list
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="sellerProfilePageWrapper">
      <div className="sellerProfilePageHeader">
        <button className="sellerProfileBackBtn" onClick={handleBack}>
          <i className="material-icons">arrow_back</i>
          <span>Back to seller profiles</span>
        </button>
        <div className="sellerProfilePageTitleRow">
          <h2 className="sellerProfilePageTitle">{headerName}</h2>
          {dirty && <span className="sellerProfileDirtyBadge">Unsaved changes</span>}
        </div>
      </div>

      <div className="sellerProfilePageBody">
        <SellerProfileFormBody profile={profile} setProfile={setProfile} />
      </div>

      <div className="sellerProfilePageFooter">
        <button className="drPopupBtn secondary" onClick={handleBack} disabled={saving}>
          Cancel
        </button>
        <button
          className="drPopupBtn primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving
            ? "Saving…"
            : isCreate
            ? "Save Seller Profile"
            : "Save Changes"}
        </button>
      </div>
    </section>
  );
});

export default withRouter(SellerProfilePage);
